import React, { useState, useEffect } from 'react';
import { Button, Paper, Stack, TextInput, FileInput, Flex } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { inject, observer } from 'mobx-react';
import * as z from 'zod';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { DocumentApi } from '../../../global/apis/Documents/DocumentApi';
import { PatientStore } from '../../../global/Store/PatientStore';
import { RouteStore } from '../../../global/Store/RouteStore';
import { useGetDocumentById } from '../../../global/entities/Document/queries/useGetDocument';
import { usePutDocumentMutations } from '../../../global/entities/Document/mutations/usePutDocumentMutations';
import { usePostDocumentMutations } from '../../../global/entities/Document/mutations/usePostDocumentMutations';
import { IDocument } from '../../../global/entities/Document/Document';
import { useGetDocuments } from '../../../global/entities/Document/queries/useGetDocuments';
import { useSessionProvider } from '../../../global/provider/sessionContext';

interface IDocumentFormProps {
  patientStore?: PatientStore;
  routeStore?: RouteStore;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_EXTENSIONS = ['pdf', 'txt', 'jpg', 'jpeg', 'png', 'avi', 'mpg', 'mov', 'mp4', 'mpeg', 'mkv'];

const getFileExtension = (filename: string): string => {
  const lastDot = filename.lastIndexOf('.');
  return lastDot === -1 ? '' : filename.slice(lastDot + 1).toLowerCase();
};

const schema = z.object({
  name: z.string().min(1, { message: t('formError.requiredName') }),
  file: z
    .instanceof(File)
    .refine((file) => !file || file.size <= MAX_FILE_SIZE, {
      message: t('formError.fileTooLarge', { max: '10MB' }),
    })
    .refine((file) => !file || ALLOWED_EXTENSIONS.includes(getFileExtension(file.name)), {
      message: t('formError.invalidFileFormat', { formats: ALLOWED_EXTENSIONS.join(', ') }),
    })
    .optional(),
});

const DocumentForm: React.FC<IDocumentFormProps> = inject('patientStore', 'routeStore')(
  observer(({ patientStore, routeStore }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    
    const { currentUser } = useSessionProvider();
    const { data: document } = useGetDocumentById();
    const putDocumentMutations = usePutDocumentMutations();
    const postDocumentMutations = usePostDocumentMutations();
    const { refetch: refetchDocuments } = useGetDocuments(patientStore?.patientId || currentUser?.patientID);

    const form = useForm({
      mode: 'controlled',
      initialValues: {
        name: '',
        file: undefined as File | undefined,
      },
      validate: zodResolver(schema),
    });

    useEffect(() => {
      if (!document) return;
      form.setValues(document);
    }, [document]);

    const handleSubmit = async (values: { name: string; file: File }) => {
      setIsSubmitting(true);
      try {
        if (!patientStore?.patientId) throw new Error('No patient ID');

        const payload = { 
          ...values,
          patientId: patientStore.patientId, 
          filename: values.file?.name, 
          dateAdded: new Date(), 
        };

        if (document && patientStore.patientId) {
          await putDocumentMutations.mutateAsync({
              id: document.id,
              document: payload as unknown as IDocument,
          });
        } else {
          const doc = await postDocumentMutations.mutateAsync(payload as unknown as IDocument);

          const policy = await DocumentApi.getUploadUrl(patientStore?.patientId || currentUser?.patientID || '', doc.id.toString(), encodeURIComponent(values.file.name));
          
          const formData = new FormData();
          formData.append('key', policy.key);
          formData.append('policy', policy.policy);
          formData.append('x-amz-algorithm', policy['x-amz-algorithm']);
          formData.append('x-amz-credential', policy['x-amz-credential']);
          formData.append('x-amz-date', policy['x-amz-date']);
          formData.append('x-amz-signature', policy['x-amz-signature']);
          formData.append('file', values.file);

          const s3Response = await fetch(policy.url, {
            method: 'POST',
            body: formData,
          });
          if (!s3Response.ok) {
            const body = await s3Response.text().catch(() => '');
            throw new Error(`S3 upload failed (${s3Response.status}): ${body.slice(0, 500)}`);
          }
        }
        await refetchDocuments();
        setIsSubmitting(false);
        navigate(routeStore?.lastRoute || '..');
      } catch (error) {
        console.error('Document upload failed:', error);
        notifications.show({
          title: t('error.uploadFailed', { defaultValue: 'Upload failed' }),
          message: error instanceof Error ? error.message : String(error),
          color: 'red',
          autoClose: 8000,
        });
        setIsSubmitting(false);
      }
    };

    return (
      <Paper h={'min-content'} w={'100%'}>
        <form
          onSubmit={form.onSubmit((values) => {
            handleSubmit(values as { name: string; file: File });
          })}
        >
          <Stack w={'100%'} gap={'md'}>
            <TextInput
              label={t('table.documents.documentName')}
              placeholder={t('table.documents.documentName')}
              {...form.getInputProps('name')}
              withAsterisk
            />
            {document ? (
              <TextInput
                label={t('table.documents.uploadFile')}
                placeholder={t('table.documents.chooseFile')}
                value={document?.filename || ''}
                readOnly
                variant="filled"
                withAsterisk
              />
            ) : (
              <FileInput
                label={t('table.documents.uploadFile')}
                placeholder={t('table.documents.chooseFile')}
                accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
                {...form.getInputProps('file')}
                withAsterisk
                error={form.errors.file}
              />
            )}
            <Flex gap={'sm'} justify={'flex-end'} w={'100%'}>
              <Button
                disabled={isSubmitting}
                type={'button'}
                onClick={() => navigate(routeStore?.lastRoute || '..')}
                variant="default"
              >
                {t('button.cancel')}
              </Button>
              <Button disabled={isSubmitting} type={'submit'}>
                {t('button.save')}
              </Button>
            </Flex>
          </Stack>
        </form>
      </Paper>
    );
  })
);

export default DocumentForm; 